import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Search,
  X,
  Building2,
  Check,
  ArrowLeft,
  Factory,
  Filter,
  Pencil,
  ChevronRight
} from "lucide-react";
import { RiResetLeftFill } from "react-icons/ri";
import { BsEnvelopeFill, BsWhatsapp, BsGlobe2 } from "react-icons/bs";
import { toast } from "sonner";
import { industries, segments, states } from "../../Components/Enums/DataEnums";
import { getListingAgenciesAPI } from "./ListingAPI";
import { getUploadUrl } from "../../Utils/Utilities";
import { getAuthData } from "../../Utils/AuthUtils";
import { createWhatsAppLink } from "../../Utils/ContactLogUtils";
import { SHOW_LISTING } from "../../Utils/FeatureFlags";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Input } from "../../Components/UI/input";
import { Button } from "../../Components/UI/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../../Components/UI/tooltip";
import { CommonFilter } from "../../Components/Common/CommonFilter";
import DrawerFilter from "../../Components/Common/DrawerFilter";
import { Drawer, DrawerContent, DrawerFooter, DrawerClose } from "../../Components/UI/drawer";
import SahiIcon from "../../Components/Common/SahiIcon";
import useIsMobile from "../../Hooks/useIsMobile";
import { useDispatch, useSelector } from "react-redux";
import { updateHeaderConfig, getCntTypewiseWebstrFromSCRM, getAgencyDetails } from "../../Store/Reducers";
import { cn } from "../../Lib/Cn";

// ============================================================================
// Feature Flag: Directory State Selection Toggle
// Set to false to hide the State selection option and restrict directory to Industry only
// ============================================================================
const SHOW_STATE_SELECTION = false;

// ============================================================================
// Static Options & Lookup Helpers
// ============================================================================
const INDUSTRY_OPTIONS = industries.map((i) => ({ ID: i.IndustryID, name: i.Industry }));
const ALL_SEGMENT_OPTIONS = segments.map((s) => ({ ID: s.SegmentID, name: s.Segment }));

// Compute filtered industries based on CntTypewiseWebstr in SCRMConfigs for the left sidebar listing
const getAvailableIndustries = (customCntTypewiseWebstr) => {
  const CntTypewiseWebstr = customCntTypewiseWebstr !== undefined ? customCntTypewiseWebstr : getCntTypewiseWebstrFromSCRM();

  const activeIndustryIDs = new Set();
  let hasIndustryConfig = false;

  if (Array.isArray(CntTypewiseWebstr) && CntTypewiseWebstr.length > 0) {
    CntTypewiseWebstr.forEach((item) => {
      const count = Number(item.CNT) || 0;
      if (item.TYPE === "Industry") {
        hasIndustryConfig = true;
        if (count > 0) {
          activeIndustryIDs.add(Number(item.IID));
        }
      }
    });
  }

  const filteredIndustries = hasIndustryConfig
    ? industries.filter((ind) => activeIndustryIDs.has(Number(ind.IndustryID)))
    : industries;

  return filteredIndustries;
};

const getIndustryName = (id) => {
  const num = Number(id);
  return industries.find((i) => Number(i.IndustryID) === num)?.Industry || "";
};

const getStateName = (id) => {
  const num = Number(id);
  return states.find((s) => Number(s.StateID) === num)?.State || "";
};

const getSegmentName = (id) => {
  const num = Number(id);
  return segments.find((s) => Number(s.SegmentID) === num)?.Segment || "";
};

const getSegmentOptions = (industryID) => {
  const num = Number(industryID);
  if (!num || industryID === "all") return ALL_SEGMENT_OPTIONS;
  return segments
    .filter((s) => Number(s.IndustryID) === num)
    .map((s) => ({ ID: s.SegmentID, name: s.Segment }));
};

const formatCityName = (str) => {
  if (!str) return "";
  return str
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};



const defaultFilters = {
  IndustryID: "all",
  StateID: "all",
  SegmentID: "all",
  City: "all",
};

const commonFilterProps = {
  showClearButton: true,
  isMobileDropdown: false,
};

const getFilterList = ({ mode, filters, setFilters, segmentOptions, stateOptions, cityOptions }) => [
  mode === "state"
    ? {
      id: "IndustryID",
      label: "All Industries",
      mobileLabel: "Industry",
      options: INDUSTRY_OPTIONS,
      value: filters.IndustryID,
      onChange: (val) => setFilters((prev) => ({ ...prev, IndustryID: val, SegmentID: "all" })),
    }
    : {
      id: "StateID",
      label: "All States",
      mobileLabel: "State",
      options: stateOptions,
      value: filters.StateID,
      onChange: (val) => setFilters((prev) => ({ ...prev, StateID: val })),
    },
  {
    id: "SegmentID",
    label: "All Segments",
    mobileLabel: "Segment",
    options: segmentOptions,
    value: filters.SegmentID,
    onChange: (val) => setFilters((prev) => ({ ...prev, SegmentID: val })),
  },
  {
    id: "City",
    label: "All Cities",
    mobileLabel: "City",
    options: cityOptions,
    value: filters.City,
    onChange: (val) => setFilters((prev) => ({ ...prev, City: val })),
  },
];

// ============================================================================
// Reusable UI Subcomponents
// ============================================================================
const ModeToggle = ({ mode, onChange, className }) => {
  const tabs = SHOW_STATE_SELECTION ? ["industry", "state"] : ["industry"];
  return (
    <div className={cn("grid bg-zinc-100/90 rounded-md border border-zinc-200/70 p-0.5", tabs.length > 1 ? "grid-cols-2" : "grid-cols-1", className)}>
      {tabs.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onChange(tab)}
          className={cn(
            "py-1.5 text-sm font-normal rounded capitalize transition-all",
            mode === tab
              ? "bg-sahi-white text-zinc-900 shadow-xs border border-zinc-200/80"
              : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200/50"
          )}
        >
          {tab}
        </button>
      ))}
    </div>
  );
};

const SearchInput = ({ value, onChange, placeholder = "Search", inputRef, className, autoFocus = false }) => (
  <div className={cn("relative flex items-center flex-grow rounded-md filter-border bg-white h-10", className)}>
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dropdown-white pointer-events-none" />
    <Input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-9 pr-8 !h-full !py-0 leading-normal border-none focus:outline-none focus:ring-0 shadow-none bg-transparent text-sm font-normal rounded-md w-full placeholder:text-muted-foreground flex items-center"
      autoFocus={autoFocus}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange("")}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none"
        title="Clear"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

const EmptyState = ({ icon: Icon, title, description, actionText, onAction }) => (
  <div className="flex-1 h-full min-h-[360px] sm:min-h-[440px] lg:min-h-[500px] flex flex-col items-center justify-center py-10 lg:py-16 px-4 text-center">
    <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-28 lg:h-28 rounded-full mx-auto mb-3.5 sm:mb-4 lg:mb-6 flex items-center justify-center bg-white shadow-sm lg:shadow-md border border-zinc-200/70 transition-transform">
      <Icon className="w-8 h-8 sm:w-10 sm:h-10 lg:w-14 lg:h-14 text-zinc-400" strokeWidth={1.75} />
    </div>
    <h4 className="font-semibold text-base lg:text-lg text-text-color tracking-tight">{title}</h4>
    <p className="text-sm text-zinc-500 mt-1.5 lg:mt-2 max-w-xs sm:max-w-md mx-auto leading-relaxed">{description}</p>
    {actionText && (
      <Button
        type="button"
        onClick={onAction}
        className="mt-5 lg:mt-6 sahi-primary-button inline-flex items-center gap-2 text-sm font-medium px-4 py-2 lg:px-5 lg:py-2.5 shadow-xs"
      >
        <Icon className="w-4 h-4" />
        <span>{actionText}</span>
      </Button>
    )}
  </div>
);

// ============================================================================
// Agency Card Component
// ============================================================================
// Layout:
// - Top: Business Name
// - Bottom Left: City, State & Action Squares (WebStore, WhatsApp, Email - optional)
// - Bottom Right: Square Logo (only rendered if logo exists)
// Tagline has been removed from frontend UI.
const AgencyCard = ({ agency, isMobile }) => {
  const navigate = useNavigate();
  const myAgency = useSelector(getAgencyDetails);
  const [imgError, setImgError] = useState(false);
  const logoUrl = agency.Logo
    ? (agency.Logo.startsWith("http") ? agency.Logo : getUploadUrl(`Uploads/${agency.Logo}`))
    : "";
  const hasLogo = Boolean(logoUrl) && !imgError;

  const displayCity = formatCityName(agency.City);
  const locationText = [displayCity, agency.State]
    .filter(Boolean)
    .map((s) => String(s).trim())
    .filter(Boolean)
    .join(", ");

  const segmentName = agency.Segment || getSegmentName(agency.SegmentID);

  const email = (agency.Email || "").trim();
  const isValidEmail = Boolean(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

  const phone = agency.Mobile ? String(agency.Mobile).replace(/\D/g, "") : "";
  const isValidPhone = Boolean(phone);

  return (
    <div
      id={`agency-card-${agency.AgencyID}`}
      onClick={() => agency.AgencyID && openWebstore(agency.AgencyID, isMobile, navigate)}
      className={cn(
        "bg-sahi-white rounded-lg border border-zinc-200 hover:border-zinc-300 hover:shadow-[0_2px_10px_rgba(0,0,0,0.035)] transition-all duration-300 ease-out flex justify-between gap-3 overflow-hidden group shadow-xs h-full p-3 sm:p-3.5",
        !isMobile && "cursor-pointer"
      )}
    >
      {/* Left Section: Business Name, Segment, Location, and Action Buttons */}
      <div className="flex flex-col justify-between min-w-0 flex-1 gap-2.5">
        {/* Business Name, Segment & Location (Clickable area with Tooltip) */}
        {!isMobile && agency.AgencyID ? (
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <div className="min-w-0">
                {/* Business Name */}
                <h3 className="text-base sm:text-lg font-bold text-zinc-900 truncate group-hover:text-sahi-primary-color transition-colors leading-snug">
                  {agency.AgencyName}
                </h3>

                {/* Segment Name & Location */}
                <div className="mt-1 min-w-0">
                  {segmentName && (
                    <p className="text-xs text-zinc-500 truncate leading-tight mb-1">
                      {segmentName}
                    </p>
                  )}
                  {/* City, State */}
                  <div className="text-xs text-zinc-500 min-w-0">
                    {locationText ? (
                      <span className="truncate block">{locationText}</span>
                    ) : (
                      <span className="invisible text-xs select-none">Location</span>
                    )}
                  </div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent
              className="listing-webstore-tooltip !min-w-0 !w-max"
              style={{ minWidth: 0, width: "max-content" }}
            >
              <p>Click to open Web Store</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="min-w-0">
            {/* Business Name */}
            <h3 className="text-base sm:text-lg font-bold text-zinc-900 truncate group-hover:text-sahi-primary-color transition-colors leading-snug">
              {agency.AgencyName}
            </h3>

            {/* Segment Name & Location */}
            <div className="mt-1 min-w-0">
              {segmentName && (
                <p className="text-xs text-zinc-500 truncate leading-tight mb-1">
                  {segmentName}
                </p>
              )}
              {/* City, State */}
              <div className="text-xs text-zinc-500 min-w-0">
                {locationText ? (
                  <span className="truncate block">{locationText}</span>
                ) : (
                  <span className="invisible text-xs select-none">Location</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Items: On desktop: WebStore button + WhatsApp + Email. On mobile: 3 icons with transparent background */}
        <div
          className={cn(
            "flex items-center shrink-0",
            isMobile ? "gap-4 py-1" : "gap-2.5 sm:gap-3"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 1. WebStore Button (Desktop: button with text and bg, Mobile: icon only with transparent bg) */}
          {agency.AgencyID && (
            !isMobile ? (
              <Tooltip delayDuration={700}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openWebstore(agency.AgencyID, isMobile, navigate);
                    }}
                    className="h-7.5 sm:h-8 px-2.5 rounded-md bg-gray-200 hover:bg-gray-300/80 text-zinc-700 flex items-center gap-1.5 transition-all active:scale-95 shrink-0"
                    aria-label="Open WebStore"
                  >
                    <BsGlobe2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    <span className="text-xs font-medium whitespace-nowrap">Visit Web Store</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  className="listing-webstore-tooltip !min-w-0 !w-max"
                  style={{ minWidth: 0, width: "max-content" }}
                >
                  <p>Click to open Web Store</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openWebstore(agency.AgencyID, isMobile, navigate);
                }}
                className="text-zinc-700 hover:opacity-80 flex items-center justify-center transition-all active:scale-90"
                aria-label="Open WebStore"
              >
                <BsGlobe2 className="w-4.5 h-4.5 shrink-0" />
              </button>
            )
          )}

          {/* 2. WhatsApp Square / Icon */}
          {isValidPhone && (() => {
            const handleWhatsApp = (e) => {
              e.stopPropagation();
              const cc = agency.CC || (phone.length === 10 ? "91" : "");
              const myName = getAuthData()?.UserName?.trim() || "";
              const myBusinessName = myAgency?.AgencyName?.trim() || "";
              const intro = myName && myBusinessName
                ? `Hi, I am ${myName} from ${myBusinessName}!`
                : myBusinessName
                  ? `Hi, I am from ${myBusinessName}!`
                  : myName
                    ? `Hi, I am ${myName}!`
                    : `Hi!`;
              const msg = `${intro}\nI found your business on SahiClub, Can we connect?`;
              window.open(createWhatsAppLink(cc, phone, msg), "_blank");
            };

            return !isMobile ? (
              <Tooltip delayDuration={700}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleWhatsApp}
                    className="w-7.5 h-7.5 sm:w-8 sm:h-8 rounded-md bg-sahi-green-light hover:opacity-80 text-sahi-green flex items-center justify-center transition-all active:scale-95"
                    aria-label="Chat on WhatsApp"
                  >
                    <BsWhatsapp className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  className="listing-webstore-tooltip !min-w-0 !w-max"
                  style={{ minWidth: 0, width: "max-content" }}
                >
                  <p>{agency.Mobile || phone}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                type="button"
                onClick={handleWhatsApp}
                className="text-sahi-green hover:opacity-80 flex items-center justify-center transition-all active:scale-90"
                aria-label="Chat on WhatsApp"
              >
                <BsWhatsapp className="w-4.5 h-4.5 shrink-0" />
              </button>
            );
          })()}

          {/* 3. Email Square / Icon (optional) */}
          {isValidEmail && (
            !isMobile ? (
              <Tooltip delayDuration={700}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`mailto:${email}`);
                    }}
                    className="w-7.5 h-7.5 sm:w-8 sm:h-8 rounded-md bg-sahi-orange-light hover:opacity-80 text-sahi-orange flex items-center justify-center transition-all active:scale-95"
                    aria-label="Send Email"
                  >
                    <BsEnvelopeFill className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  className="listing-webstore-tooltip !min-w-0 !w-max"
                  style={{ minWidth: 0, width: "max-content" }}
                >
                  <p>{email}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`mailto:${email}`);
                }}
                className="text-sahi-orange hover:opacity-80 flex items-center justify-center transition-all active:scale-90"
                aria-label="Send Email"
              >
                <BsEnvelopeFill className="w-4.5 h-4.5 shrink-0" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Right Section: Logo in Top Right (only show if logo is there, else dont render logo) */}
      {hasLogo && (
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-md flex items-center justify-center shrink-0 overflow-hidden self-start">
          <img
            src={logoUrl}
            alt={agency.AgencyName || "Logo"}
            className="w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
            onError={() => setImgError(true)}
          />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Mobile Fullscreen Drawer: Filters
// ============================================================================
const FilterDrawer = ({ open, onClose, filters, onApply, mode, selectedID, stateOptions, cityOptions }) => {
  const [draftFilters, setDraftFilters] = useState(filters);

  useEffect(() => {
    if (open) {
      setDraftFilters(filters);
    }
  }, [open, filters]);

  const effectiveIndustryID = mode === "industry" ? selectedID : draftFilters.IndustryID;
  const segmentOptions = getSegmentOptions(effectiveIndustryID);

  const filterList = getFilterList({
    mode,
    filters: draftFilters,
    setFilters: setDraftFilters,
    segmentOptions,
    stateOptions,
    cityOptions,
  });

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DrawerContent className="p-0 [&>div:first-child]:hidden">
        <div className="flex flex-col h-[100dvh]">
          {/* Header */}
          <div className="flex plain items-center justify-between p-4 border-b-2 border-zinc-200 shrink-0">
            <div className="flex gap-4 items-center">
              <DrawerClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                <ArrowLeft className="h-6 w-6 text-zinc-700 hover:text-zinc-900" onClick={onClose} />
                <span className="sr-only">Back</span>
              </DrawerClose>
              <p className="text-lg font-semibold text-sahi-primary-color">Listing Filters</p>
            </div>
            <button
              type="button"
              onClick={() => setDraftFilters(defaultFilters)}
              className="group flex items-center gap-1 text-sm font-light hover:underline text-sahi-primary-color"
            >
              <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
              <span>Reset</span>
            </button>
          </div>

          {/* Scrollable Filters */}
          <div className="flex flex-1 overflow-hidden">
            <div className="flex-1 p-3 overflow-y-auto filter-scroll-container">
              {filterList.map((opt) => (
                <div key={opt.id} id={opt.id} className="mb-4">
                  <DrawerFilter
                    id={opt.id}
                    label={opt.label}
                    options={opt.options}
                    value={opt.value}
                    onChange={opt.onChange}
                    type="list"
                    mobileDropdownText={`Select ${opt.mobileLabel}`}
                    searchEnabled={true}
                    MobileLabel={opt.mobileLabel}
                    showAllOption={true}
                    allText={opt.label}
                    allID="all"
                    searchThreshold={10}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-white shrink-0">
            <DrawerClose asChild>
              <div className="flex justify-start gap-2">
                <Button
                  className="sahi-primary-button"
                  onClick={() => {
                    onApply(draftFilters);
                    onClose();
                  }}
                >
                  Apply
                </Button>
                <Button variant="outline" className="sahi-secondary-button" onClick={onClose}>
                  Cancel
                </Button>
              </div>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

// Module-level cache to persist selection, filters, and opened flag across unmount/remount (e.g. going to in-app WebStore and back)
const cachedListing = {
  mode: SHOW_STATE_SELECTION ? "industry" : "industry",
  selectedID: 0,
  filters: defaultFilters,
  searchQuery: "",
  hasAutoOpened: false,
  lastViewedAgencyID: null,
  isNavigatingToWebstore: false,
};

const resetCachedListing = () => {
  cachedListing.mode = SHOW_STATE_SELECTION ? "industry" : "industry";
  cachedListing.selectedID = 0;
  cachedListing.filters = defaultFilters;
  cachedListing.searchQuery = "";
  cachedListing.hasAutoOpened = false;
  cachedListing.lastViewedAgencyID = null;
  cachedListing.isNavigatingToWebstore = false;
};

const openWebstore = (agencyID, isMobile = false, navigate = null) => {
  if (!agencyID) return;
  if (isMobile && navigate) {
    cachedListing.lastViewedAgencyID = agencyID;
    cachedListing.isNavigatingToWebstore = true;
    navigate(`/store?m=${agencyID}&i=1`);
    return;
  }
  window.open(`${window.location.origin}/web/store?m=${agencyID}`, "_blank", "noopener,noreferrer");
};

// ============================================================================
// Main Listing Component
// ============================================================================
export default function Listing() {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // The selected directory item is reflected in the URL (?ind=<id>) so that browser
  // back/forward and the in-app WebStore round-trip restore the right view natively.
  const baseListingPath = location.pathname.toLowerCase().startsWith("/directory") ? "/directory" : "/listing";
  const getIndFromSearch = (search) => {
    const raw = new URLSearchParams(search).get("ind");
    return raw ? Number(raw) || 0 : 0;
  };

  // Reset the navigating flag upon mounting
  cachedListing.isNavigatingToWebstore = false;

  // Clear cache on component unmount unless the user is navigating to in-app WebStore
  useEffect(() => {
    return () => {
      if (!cachedListing.isNavigatingToWebstore) {
        resetCachedListing();
      }
    };
  }, []);

  // Directory Selection State
  // Mode: "industry" | "state"
  const [mode, setMode] = useState(SHOW_STATE_SELECTION ? cachedListing.mode : "industry");
  // Initial selection comes from the URL so deep links and back/forward work.
  const [selectedID, setSelectedID] = useState(() => getIndFromSearch(window.location.search));

  // Available industries filtered by CntTypewiseWebstr in SCRMConfigs for the left sidebar
  const reduxCntTypewiseWebstr = useSelector((state) => state.scrmConfigs?.CntTypewiseWebstr);
  const availableIndustries = useMemo(
    () => getAvailableIndustries(reduxCntTypewiseWebstr),
    [reduxCntTypewiseWebstr]
  );

  // Filters & Search State
  const [filters, setFilters] = useState(cachedListing.filters);
  const [searchQuery, setSearchQuery] = useState(cachedListing.searchQuery);
  const [sidebarSearch, setSidebarSearch] = useState("");

  // Drawers & Modals Visibility
  const [isDirectoryDrawerOpen, setIsDirectoryDrawerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const mobileSearchInputRef = useRef(null);

  // Data & Loading State
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasAutoOpenedRef = useRef(cachedListing.hasAutoOpened);

  // Keep module-level cache in sync with local state
  useEffect(() => {
    cachedListing.mode = mode;
    cachedListing.selectedID = selectedID;
    cachedListing.filters = filters;
    cachedListing.searchQuery = searchQuery;
  }, [mode, selectedID, filters, searchQuery]);

  const hasSelection = Number(selectedID) > 0;
  const selectedName = mode === "industry" ? getIndustryName(selectedID) : getStateName(selectedID);

  // Keep the selected directory item in sync with the URL. When the URL changes
  // (browser back/forward, WebStore round-trip), update the selection and clear filters.
  useEffect(() => {
    const fromUrl = getIndFromSearch(location.search);
    if (Number(selectedID) !== fromUrl) {
      setSelectedID(fromUrl);
      setFilters(defaultFilters);
      setSearchQuery("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Auto-focus mobile search input when opened
  useEffect(() => {
    if (showMobileSearch) {
      const timer = setTimeout(() => {
        mobileSearchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showMobileSearch]);

  // Fetch agencies from backend via b2b_GetListing API
  useEffect(() => {
    if (!SHOW_LISTING()) return;
    let isActive = true;

    if (hasSelection) {
      setLoading(true);
      const payload = {
        industryID: mode === "industry" ? Number(selectedID) : 0,
        segmentID: 0,
        stateID: mode === "state" ? Number(selectedID) : 0,
      };

      getListingAgenciesAPI(payload)
        .then((data) => {
          if (isActive) {
            setAgencies(Array.isArray(data) ? data : []);
          }
        })
        .catch((error) => {
          console.error("Error fetching listings:", error);
          if (isActive) {
            toast.error("Failed to load business directory.");
            setAgencies([]);
          }
        })
        .finally(() => {
          if (isActive) setLoading(false);
        });
    } else {
      setAgencies([]);
      setLoading(false);
    }

    return () => {
      isActive = false;
    };
  }, [mode, selectedID, hasSelection]);

  // Restore scroll position to the agency card the user clicked before opening webstore
  useEffect(() => {
    if (!loading && agencies.length > 0 && cachedListing.lastViewedAgencyID) {
      const agencyID = cachedListing.lastViewedAgencyID;
      const timer = setTimeout(() => {
        const el = document.getElementById(`agency-card-${agencyID}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        cachedListing.lastViewedAgencyID = null;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, agencies]);

  // Switch directory mode (Industry / State)
  const handleSwitchMode = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setSelectedID(0);
    setAgencies([]);
    setLoading(false);
    setSidebarSearch("");
    setFilters(defaultFilters);
    setSearchQuery("");
    cachedListing.selectedID = 0;
    cachedListing.lastViewedAgencyID = null;
  };

  const handleSelectDirectoryItem = (newMode, id) => {
    const isModeChanged = newMode !== mode;
    const isSelectionChanged = Number(id) !== Number(selectedID);

    setMode(newMode);

    if (isModeChanged || isSelectionChanged) {
      setSelectedID(id);
      setFilters(defaultFilters);
      setSearchQuery("");
      // Clear previous results and show the skeleton immediately so the
      // "No Listings" empty state does not flash before the fetch effect runs.
      setAgencies([]);
      setLoading(true);
      // Push a history entry so browser back returns to the selection list.
      navigate(`${baseListingPath}?ind=${id}`);
    }
  };

  const handleResetDirectory = () => {
    setSelectedID(0);
    setFilters(defaultFilters);
    setSearchQuery("");
    cachedListing.selectedID = 0;
    cachedListing.lastViewedAgencyID = null;
    // Drop the ?ind entry so back doesn't re-enter the selected view.
    if (getIndFromSearch(location.search) !== 0) {
      navigate(baseListingPath, { replace: true });
    }
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setSearchQuery("");
    setShowMobileSearch(false);
  };

  // Segment options scoping for desktop top filters
  const currentIndustryID = mode === "industry" ? selectedID : filters.IndustryID;
  const segmentOptions = getSegmentOptions(currentIndustryID);

  // Distinct state options from loaded agencies (only show states fetched from backend)
  const stateOptions = useMemo(() => {
    const stateMap = new Map();
    agencies.forEach((a) => {
      let sId = Number(a.StateID);
      let sName = (a.State || "").trim();
      if (!sId && sName) {
        const match = states.find((s) => s.State.toLowerCase() === sName.toLowerCase());
        if (match) sId = match.StateID;
      }
      if (sId && !sName) {
        sName = getStateName(sId);
      }
      if (sId && sName && !stateMap.has(sId)) {
        stateMap.set(sId, sName);
      }
    });
    return Array.from(stateMap.entries())
      .map(([id, name]) => ({ ID: id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [agencies]);

  // Distinct city options from loaded agencies (case-insensitive deduplication)
  const cityOptions = useMemo(() => {
    const cityMap = new Map();
    agencies.forEach((a) => {
      const raw = (a.City || "").trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (!cityMap.has(key)) {
        cityMap.set(key, formatCityName(raw));
      }
    });
    return Array.from(cityMap.values())
      .sort((a, b) => a.localeCompare(b))
      .map((c) => ({ ID: c, name: c }));
  }, [agencies]);

  // Client-side filtering & sorting (Sorted alphabetically by AgencyName)
  const filteredAgencies = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filterSeg = Number(filters.SegmentID) || 0;
    const filterInd = mode === "state" ? Number(filters.IndustryID) || 0 : 0;
    const filterState = mode === "industry" ? Number(filters.StateID) || 0 : 0;
    const filterCity = filters.City && filters.City !== "all" ? filters.City.trim().toLowerCase() : "";

    return agencies.filter((agency) => {
      const agencyIndustryID = Number(agency.IndustryID);
      const agencySegmentID = Number(agency.SegmentID);

      if (filterSeg > 0 && agencySegmentID !== filterSeg) return false;
      if (filterInd > 0 && agencyIndustryID !== filterInd) return false;
      if (filterState > 0) {
        const agencyStateId = Number(agency.StateID);
        if (agencyStateId > 0) {
          if (agencyStateId !== filterState) return false;
        } else if (agency.State) {
          const matchState = states.find((s) => s.State.toLowerCase() === agency.State.trim().toLowerCase());
          if (matchState?.StateID !== filterState) return false;
        } else {
          return false;
        }
      }

      if (filterCity) {
        const city = (agency.City || "").toLowerCase();
        if (!city.includes(filterCity)) return false;
      }

      if (q) {
        const name = (agency.AgencyName || "").toLowerCase();
        const tagline = (agency.Tagline || "").toLowerCase();
        const indName = (agency.Industry || getIndustryName(agencyIndustryID)).toLowerCase();
        const segName = (agency.Segment || getSegmentName(agencySegmentID)).toLowerCase();
        if (!name.includes(q) && !tagline.includes(q) && !indName.includes(q) && !segName.includes(q)) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => {
      const nameA = (a.AgencyName || "").trim();
      const nameB = (b.AgencyName || "").trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base", numeric: true });
    });
  }, [agencies, searchQuery, mode, filters]);



  // Sidebar category filtering
  const qSidebar = sidebarSearch.trim().toLowerCase();
  const rawSidebarList = mode === "industry" ? availableIndustries : states;
  const sidebarKey = mode === "industry" ? "Industry" : "State";
  const sidebarItems = qSidebar
    ? rawSidebarList.filter((item) => (item[sidebarKey] || "").toLowerCase().includes(qSidebar))
    : rawSidebarList;

  // Window event listeners for mobile header buttons dispatched by Main.js
  useEffect(() => {
    const handleToggleSearch = () => {
      setShowMobileSearch((prev) => {
        if (prev) {
          setSearchQuery("");
          setSidebarSearch("");
        }
        return !prev;
      });
    };
    const handleToggleFilter = () => setIsFilterDrawerOpen(true);

    window.addEventListener("listing-toggle-search", handleToggleSearch);
    window.addEventListener("listing-toggle-filter", handleToggleFilter);

    return () => {
      window.removeEventListener("listing-toggle-search", handleToggleSearch);
      window.removeEventListener("listing-toggle-filter", handleToggleFilter);
    };
  }, []);

  // Update Redux header configuration for mobile
  useEffect(() => {
    if (isMobile) {
      const hasSecondaryFilters = (
        filters.IndustryID !== "all" ||
        filters.StateID !== "all" ||
        filters.SegmentID !== "all" ||
        filters.City !== "all"
      );

      dispatch(
        updateHeaderConfig({
          title: "Directory",
          iconLeft: {
            icon: ArrowLeft,
            onClick: () => navigate("/", { replace: true }),
          },
          actionsRight: [
            {
              icon: Search,
              onClick: () => setShowMobileSearch((prev) => !prev),
            },
            {
              icon: Filter,
              onClick: () => setIsFilterDrawerOpen(true),
              showBadge: hasSecondaryFilters,
              badgeColor: "#0B5D1E",
            },
          ],
        })
      );
    }
  }, [isMobile, dispatch, filters]);

  const hasActiveFilters = Boolean(
    searchQuery ||
    filters.IndustryID !== "all" ||
    filters.StateID !== "all" ||
    filters.SegmentID !== "all" ||
    filters.City !== "all"
  );

  const totalCountText = agencies.length > 0 && filteredAgencies.length === 0
    ? `0 of ${agencies.length}`
    : filteredAgencies.length;

  if (!SHOW_LISTING()) {
    return <Navigate to="/community" replace />;
  }

  return (
    <TooltipProvider delayDuration={700} skipDelayDuration={0} disableHoverableContent={true}>
      <div className="h-full w-full flex flex-col overflow-hidden">
        {/* Scoped dropdown popover sizing when browsing Directory */}
        <style>{`
        [data-radix-popper-content-wrapper] > div[role="dialog"],
        [data-radix-popper-content-wrapper] > div:has([cmdk-root]) {
          min-width: 260px !important;
          width: auto !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }
        [data-radix-popper-content-wrapper] [cmdk-root] {
          width: 100% !important;
          min-width: 100% !important;
          box-sizing: border-box !important;
        }
        [data-radix-popper-content-wrapper] [cmdk-list] {
          max-height: 280px !important;
        }
        .listing-webstore-tooltip,
        [data-radix-popper-content-wrapper] > div.listing-webstore-tooltip {
          min-width: 0 !important;
          width: max-content !important;
        }
        .listing-tagline-tooltip,
        [data-radix-popper-content-wrapper] > div.listing-tagline-tooltip {
          min-width: 0 !important;
          width: auto !important;
          max-width: 24rem !important;
        }
      `}</style>
        <div className="flex flex-col lg:flex-row items-stretch gap-4 lg:gap-6 w-full flex-1 min-h-0 overflow-hidden">

          {/* ======================================================== */}
          {/* LEFT SIDEBAR: "Directory" Title + Category Selection     */}
          {/* ======================================================== */}
          <aside className="hidden lg:flex w-full lg:w-72 xl:w-72 shrink-0 h-full flex-col min-h-0">
            <div className="h-10 flex items-center shrink-0 mb-3 lg:mb-6">
              <h1 className="text-3xl font-bold text-text-color web-header-name">
                Directory
              </h1>
            </div>

            <div className="bg-sahi-white rounded-lg border border-zinc-200 shadow-xs flex-1 min-h-0 flex flex-col overflow-hidden">
              {/* Search Box */}
              <div className="p-3 pb-2.5 shrink-0 bg-white rounded-t-lg space-y-2.5">
                {SHOW_STATE_SELECTION && <ModeToggle mode={mode} onChange={handleSwitchMode} />}
                <SearchInput
                  value={sidebarSearch}
                  onChange={setSidebarSearch}
                  placeholder={`Search ${SHOW_STATE_SELECTION && mode === "state" ? "states" : "industries"}...`}
                />
              </div>

              {/* Scrollable Category List */}
              <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-zinc-200 rounded-b-lg custom-scrollbar [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-zinc-200">
                {sidebarItems.map((item) => {
                  const id = mode === "industry" ? item.IndustryID : item.StateID;
                  const label = mode === "industry" ? item.Industry : item.State;
                  const isSelected = Number(selectedID) === Number(id);
                  const iconName = mode === "industry" ? item.Icon : "MapPin";

                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleSelectDirectoryItem(mode, id)}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors text-left",
                        isSelected
                          ? "bg-gray-200 text-sahi-primary-color font-medium"
                          : "hover:bg-gray-50 text-gray-800 active:bg-gray-100 font-normal"
                      )}
                      title={label}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {iconName && (
                          <SahiIcon
                            iconName={iconName}
                            className={cn(
                              "w-[18px] h-[18px] shrink-0 transition-colors",
                              isSelected ? "text-sahi-primary-color" : "text-zinc-500"
                            )}
                          />
                        )}
                        <span className="truncate pr-2">{label}</span>
                      </div>
                      {isSelected && (
                        <Check className="w-5 h-5 text-sahi-primary-color flex-shrink-0 ml-auto" />
                      )}
                    </button>
                  );
                })}

                {sidebarItems.length === 0 && (
                  <div className="p-6 text-center text-xs text-zinc-400">
                    No {mode === "industry" ? "industries" : "states"} found.
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* ======================================================== */}
          {/* RIGHT COLUMN: Top Filters Bar + Listings Grid            */}
          {/* ======================================================== */}
          <section className="flex-1 min-w-0 w-full h-full flex flex-col min-h-0 overflow-hidden">

            {/* Mobile Search Bar */}
            {isMobile && showMobileSearch && (
              <div className="w-full flex items-center gap-2 mt-3 mb-3 shrink-0">
                <SearchInput
                  value={hasSelection ? searchQuery : sidebarSearch}
                  onChange={hasSelection ? setSearchQuery : setSidebarSearch}
                  placeholder={hasSelection ? "Search" : `Search ${SHOW_STATE_SELECTION && mode === "state" ? "states" : "industries"}...`}
                  inputRef={mobileSearchInputRef}
                  className="h-10"
                  autoFocus
                />
              </div>
            )}

            {/* Mobile Status Bar */}
            {isMobile && hasSelection && (
              <div className={cn(
                "flex items-center justify-between px-1 shrink-0 gap-2 pb-3.5",
                showMobileSearch ? "pt-0" : "pt-3.5"
              )}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={handleResetDirectory}
                    className="flex items-center gap-1.5 min-w-0 flex-1 text-left group active:opacity-70 transition-opacity"
                    title="Change Industry"
                  >
                    <span className="text-sm font-semibold text-zinc-800 truncate group-hover:text-sahi-primary-color transition-colors">
                      {selectedName}
                    </span>
                    <Pencil className="w-3.5 h-3.5 text-zinc-500 group-hover:text-sahi-primary-color shrink-0 transition-colors" />
                  </button>
                </div>

                <span className="text-sm text-zinc-500 shrink-0">
                  Suppliers: <span className="font-semibold text-sahi-primary-color">
                    {totalCountText}
                  </span>
                </span>
              </div>
            )}

            {/* Desktop Top Filter Bar */}
            {hasSelection && (loading || agencies.length > 0) && (
              <div className="hidden lg:flex flex-wrap items-center justify-end gap-2 xl:gap-2.5 shrink-0 mb-3 lg:mb-6">
                {/* Total Count */}
                {!loading && (
                  <div className="w-fit bg-white text-black sahi-radius border filter-border shadow-xs px-2.5 xl:px-3 h-10 flex items-center shrink-0 ml-auto">
                    <span className="flex items-center gap-1.5 whitespace-nowrap text-xs xl:text-sm font-normal">
                      <span>Total:</span>
                      <span className="font-normal text-sahi-primary-color">
                        {totalCountText}
                      </span>
                    </span>
                  </div>
                )}

                {/* Search Box */}
                {!loading && (
                  <div className="flex-1 min-w-[140px] max-w-[200px] xl:max-w-[260px]">
                    <SearchInput
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search"
                      className="h-10"
                    />
                  </div>
                )}

                {/* Desktop Filters */}
                {loading ? (
                  <div className="h-10 invisible" aria-hidden="true" />
                ) : (
                  getFilterList({
                    mode,
                    filters,
                    setFilters,
                    segmentOptions,
                    stateOptions,
                    cityOptions,
                  }).map((filter) => (
                    <div
                      key={filter.id}
                      className={cn(
                        "[&_button]:!min-w-[150px] xl:[&_button]:!min-w-[170px] [&_button]:!max-w-[260px] xl:[&_button]:!max-w-[320px] [&_button]:!h-10 [&_button_span]:!text-sm",
                        filter.id === "SegmentID" && "xl:[&_button]:!min-w-[185px] xl:[&_button]:!max-w-[340px]"
                      )}
                    >
                      <CommonFilter
                        {...commonFilterProps}
                        label={filter.label}
                        options={filter.options}
                        value={filter.value}
                        onChange={filter.onChange}
                      />
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Scrollable Listings Grid Area */}
            <div className={cn(
              "flex-1 min-h-0 flex flex-col overflow-y-auto",
              isMobile
                ? "no-scrollbar task-list-scroll"
                : "custom-scrollbar [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-zinc-200"
            )}>
              {!hasSelection ? (
                isMobile ? (
                  <div className={cn(
                    "flex-1 min-h-0 flex flex-col",
                    showMobileSearch ? "pt-0" : "pt-3"
                  )}>
                    {/* Scrollable Category List (Card Style) */}
                    <div className="flex-1 min-h-0 overflow-y-auto space-y-2 no-scrollbar task-list-scroll pr-0.5">
                      {sidebarItems.map((item) => {
                        const id = (!SHOW_STATE_SELECTION || mode === "industry") ? item.IndustryID : item.StateID;
                        const label = (!SHOW_STATE_SELECTION || mode === "industry") ? item.Industry : item.State;
                        const isSelected = Number(selectedID) === Number(id);
                        const iconName = (!SHOW_STATE_SELECTION || mode === "industry") ? item.Icon : "MapPin";

                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => handleSelectDirectoryItem(mode, id)}
                            className={cn(
                              "w-full flex items-center justify-between px-3.5 py-3 text-sm rounded-md border transition-all text-left active:scale-[0.99]",
                              isSelected
                                ? "bg-white border-sahi-primary-color text-sahi-primary-color font-semibold shadow-xs"
                                : "bg-white border-gray-200 text-zinc-800 hover:border-gray-300 shadow-xs active:bg-gray-50 font-medium"
                            )}
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              {iconName && (
                                <SahiIcon
                                  iconName={iconName}
                                  className={cn(
                                    "w-[18px] h-[18px] shrink-0 transition-colors",
                                    isSelected ? "text-sahi-primary-color" : "text-zinc-500"
                                  )}
                                />
                              )}
                              <span className="truncate pr-2">{label}</span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                              {isSelected ? (
                                <Check className="w-4.5 h-4.5 text-sahi-primary-color flex-shrink-0" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        );
                      })}

                      {sidebarItems.length === 0 && (
                        <div className="p-8 text-center text-sm text-zinc-400">
                          No {mode === "industry" ? "industries" : "states"} found.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    icon={Factory}
                    title={`Select an ${mode === "industry" ? "Industry" : "State"}`}
                    description={`Select an ${mode === "industry" ? "industry" : "state"} from the directory on the left to explore business listings.`}
                  />
                )
              ) : loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 xl:gap-3.5">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <div
                      key={n}
                      className="bg-sahi-white rounded-lg border border-zinc-200 animate-pulse flex justify-between gap-3 overflow-hidden shadow-xs h-full p-3 sm:p-3.5"
                    >
                      {/* Left Section Skeleton: Name, Segment, Location, Actions */}
                      <div className="flex flex-col justify-between min-w-0 flex-1 gap-2.5">
                        <div className="min-w-0 space-y-2">
                          {/* Business Name Skeleton */}
                          <div className="h-5 bg-zinc-200 sahi-radius w-3/5" />

                          {/* Segment + City, State skeleton */}
                          <div className="space-y-1 min-w-0">
                            <div className="h-2.5 bg-zinc-100 sahi-radius w-2/5" />
                            <div className="h-3 bg-zinc-200 sahi-radius w-3/5" />
                          </div>
                        </div>

                        {/* Action items skeleton */}
                        <div className="flex items-center gap-3 sm:gap-3">
                          {isMobile ? (
                            <div className="w-7 h-7 rounded-md bg-zinc-200 shrink-0" />
                          ) : (
                            <div className="w-24 h-7.5 sm:h-8 rounded-md bg-zinc-200 shrink-0" />
                          )}
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-zinc-200 shrink-0" />
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-zinc-200 shrink-0" />
                        </div>
                      </div>

                      {/* Right Section: Logo square skeleton in top-right */}
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-md bg-zinc-100 shrink-0 self-start" />
                    </div>
                  ))}
                </div>
              ) : filteredAgencies.length === 0 ? (
                agencies.length === 0 ? (
                  <EmptyState
                    icon={Building2}
                    title={`No Listings in ${selectedName || (mode === "industry" ? "this Industry" : "this State")}`}
                    description={
                      isMobile
                        ? `There are currently no business listings registered for ${selectedName ? `"${selectedName}"` : `this ${mode}`}. Choose another ${mode === "industry" ? "industry" : "state"} to explore.`
                        : `There are currently no business listings registered for ${selectedName ? `"${selectedName}"` : `this ${mode}`}. Select another ${mode === "industry" ? "industry" : "state"} from the directory on the left.`
                    }
                    actionText={isMobile ? `Choose Another ${mode === "industry" ? "Industry" : "State"}` : undefined}
                    onAction={isMobile ? handleResetDirectory : undefined}
                  />
                ) : (
                  <EmptyState
                    icon={Filter}
                    title="No Listings Match Your Filters"
                    description={`None of the ${agencies.length} listing${agencies.length === 1 ? "" : "s"} in ${selectedName ? `"${selectedName}"` : `this ${mode}`} match your current search or filter criteria.`}
                    actionText="Clear Filters"
                    onAction={handleClearFilters}
                  />
                )
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 xl:gap-3.5">
                  {filteredAgencies.map((agency) => (
                    <AgencyCard
                      key={agency.AgencyID}
                      agency={agency}
                      showIndustry={mode === "state"}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              )}

              {/* Bottom spacer for mobile to ensure last cards scroll comfortably above floating + button */}
              {isMobile && hasSelection && filteredAgencies.length > 0 && (
                <div className="h-20 shrink-0 w-full pointer-events-none" aria-hidden="true" />
              )}
            </div>
          </section>
        </div>

        {/* ======================================================== */}
        {/* MOBILE FULLSCREEN DRAWERS                                */}
        {/* ======================================================== */}
        {isMobile && (
          <FilterDrawer
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            filters={filters}
            onApply={(newFilters) => setFilters(newFilters)}
            mode={mode}
            selectedID={selectedID}
            stateOptions={stateOptions}
            cityOptions={cityOptions}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
